/**
 * «Пропущенные» как корзина воронки (#1337) и перекос выкатки.
 *
 * Сводка `/reports/summary` кешируется браузером (пять минут плюс
 * stale-while-revalidate), и один и тот же бандл может встретить два ответа:
 * без корзины `missed` — закешированный до выкатки или от откатанного
 * edge-api — и с ней. В первом пропущенные звонки лежат внутри «без записи
 * разговора», а список (`outcome=`) новой версии их оттуда уже убрал, и старый
 * edge-api на `outcome=missed` отвечает 400 (энум запроса не знает значения).
 * Поэтому корзина и ссылки на неё предлагаются только там, где ответ сам
 * сказал, что она есть, а список «Записей» берёт исход назад, когда сервер
 * его отверг (`isUnknownOutcomeRefusal`).
 */
import { ApiError, isObject } from '@/lib/fetch-utils';

/**
 * Параметр запроса сводки, меняющий её URL. Ключ HTTP-кеша браузера — URL:
 * без этого ответ, закешированный до выкатки, отдавался бы новому бандлу до
 * часа. Сервер параметр не читает: схема запроса лишние ключи отбрасывает, и
 * откатанный edge-api тоже.
 */
export const REPORT_SUMMARY_FUNNEL_PARAM = { funnel: 'missed' } as const;

/**
 * The list refused its `outcome` as an unknown value: an edge-api older than
 * the bucket (rolled back behind the live bundle) validates the query against
 * an enum without `missed` and answers 400. The body is the validator's own —
 * the zod issues as a JSON string in `error.message`, the issue's path naming
 * `outcome`. Any other refusal (another 400 included) is not this one.
 *
 * This is the one guard against a version skew in «Записи»: it reads the
 * list's own answer, not a guess from a cached report — a page opened directly
 * has no report, and a stale one says nothing about the server answering now.
 */
export function isUnknownOutcomeRefusal(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.status !== 400 || !isObject(error.data)) {
    return false;
  }
  const failure = error.data.error;
  if (!isObject(failure) || typeof failure.message !== 'string') return false;
  let issues: unknown;
  try {
    issues = JSON.parse(failure.message);
  } catch {
    return false;
  }
  return (
    Array.isArray(issues) &&
    issues.some(
      (issue) =>
        isObject(issue) &&
        Array.isArray(issue.path) &&
        issue.path[0] === 'outcome' &&
        (issue.code === 'invalid_value' || issue.code === 'invalid_enum_value')
    )
  );
}
